import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./carousel.scss";

const Carousel = ({ images, baseURL, visibleCount = 3 }) =>{
    const wrapperRef = useRef(null);
    const trackRef = useRef(null);

    const [slideWidth, setSlideWidth] = useState(0);
    const [index, setIndex] = useState(visibleCount);
    const [transition, setTransition] = useState(true);

    // Drag state
    const pos = useRef({
        dragging: false,
        startX: 0,
        startTime: 0,
        currentTranslate: 0,
        prevTranslate: 0
    });

    /** Build full URLs */
    const fullImages = useMemo(
        () =>
            images.map((f) => ({
                url: `${baseURL}/images/home/${f}`,
                label: "Featured Item",
            })),
        [images, baseURL]
    );

    /** Duplicate slides for infinite looping */
    const slides = useMemo(
        () => [
            ...fullImages.slice(-visibleCount),
            ...fullImages,
            ...fullImages.slice(0, visibleCount),
        ],
        [fullImages, visibleCount]
    );

    /** Measure sizes */
    useEffect(() => {
        const update = () => {
            if (!wrapperRef.current) return;
            const width = wrapperRef.current.offsetWidth / visibleCount;
            if (width > 0) setSlideWidth(width);
        };

        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [visibleCount]);

    /** Apply transform when index changes */
    useEffect(() => {
        if (!trackRef.current || slideWidth === 0) return;
        trackRef.current.style.transition = transition ? "transform 0.45s ease" : "none";
        trackRef.current.style.transform = `translateX(-${index * slideWidth}px)`;
    }, [index, slideWidth, transition]);

    // Autoplay
    useEffect(() => {
        if (slideWidth === 0) return;

        const play = () => {
            setTransition(true);
            setIndex(i => i + 1);
        };

        const timer = setInterval(play, 3000);
        return () => clearInterval(timer);

    }, [slideWidth]);


    /** Infinite loop reposition */
    const handleTransitionEnd = () => {
        if (index === slides.length - visibleCount) {
            setTransition(false);
            setIndex(visibleCount);
        } else if (index === 0) {
            setTransition(false);
            setIndex(fullImages.length);
        } else {
            setTransition(true);
        }
    };

    /** Navigation */
    const nextSlide = useCallback(() => {
        setTransition(true);
        setIndex((i) => i + 1);
    }, []);

    const prevSlide = useCallback(() => {
        setTransition(true);
        setIndex((i) => i - 1);
    }, []);

    /** --- PHYSICS SWIPE HANDLERS --- **/

    const startDrag = (x) => {
        pos.current.dragging = true;
        pos.current.startX = x;
        pos.current.startTime = performance.now();
        pos.current.prevTranslate = -index * slideWidth;
    };

    const moveDrag = (x) => {
        if (!pos.current.dragging || !trackRef.current) return;

        const diff = x - pos.current.startX;
        pos.current.currentTranslate = pos.current.prevTranslate + diff;

        trackRef.current.style.transition = "none";
        trackRef.current.style.transform = `translateX(${pos.current.currentTranslate}px)`;
    };

    const endDrag = () => {
        if (!pos.current.dragging) return;
        pos.current.dragging = false;

        const moved = pos.current.currentTranslate - pos.current.prevTranslate;
        const time = performance.now() - pos.current.startTime;
        const velocity = moved / time; // px per ms

        const swipeThreshold = 50;
        const velocityThreshold = 0.35;

        if (moved < -swipeThreshold || velocity < -velocityThreshold) {
            nextSlide();
        } else if (moved > swipeThreshold || velocity > velocityThreshold) {
            prevSlide();
        } else {
            setTransition(true);
            setIndex((i) => i);
        }
    };

    /** DOM event wrappers */
    const onTouchStart = (e) => startDrag(e.touches[0].clientX);
    const onTouchMove = (e) => moveDrag(e.touches[0].clientX);
    const onTouchEnd = endDrag;

    const onMouseDown = (e) => {
        e.preventDefault();
        startDrag(e.clientX);
    };
    const onMouseMove = (e) => {
        if (pos.current.dragging) moveDrag(e.clientX);
    };
    const onMouseUp = endDrag;
    const onMouseLeave = () => pos.current.dragging && endDrag();

    /** Indicator calculation */
    const dotIndex = (index - visibleCount + fullImages.length) % fullImages.length;

    return (
        <div className="carouselWrapper" ref={wrapperRef}>
            <button className="carouselArrow left" onClick={prevSlide}>❮</button>
            <button className="carouselArrow right" onClick={nextSlide}>❯</button>

            <div className="carouselViewport">
                <div
                    className="carouselTrack"
                    ref={trackRef}
                    style={{ width: `${slides.length * slideWidth}px` }}
                    onTransitionEnd={handleTransitionEnd}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseLeave}
                >
                    {slides.map((img, i) => (
                        <div key={i} className="carouselSlide" style={{ minWidth: slideWidth }}>
                            <div className="carouselImgWrapper">
                                <img src={img.url} alt="" />
                                <span className="featuredLabel">{img.label}</span>
                                <div className="carouselGradient" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="carouselDots">
                {fullImages.map((_, i) => (
                    <button
                        key={i}
                        className={`dot ${i === dotIndex ? "active" : ""}`}
                        onClick={() => {
                            setTransition(true);
                            setIndex(i + visibleCount);
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

export default Carousel;